class MonogramCustomizer extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.monogram-toggle');
    this.content = this.querySelector('.monogram-content');
    this.textInput = this.querySelector('.monogram-text-input');
    this.dateInput = this.querySelector('.monogram-date-input');
    this.feeInput = this.querySelector('.monogram-fee-id');
    this.cartError = this.querySelector('.monogram-cart-error');
    this.previousGalleryIndex = null;
    
    this.previewWrappers = document.querySelectorAll('.monogram-preview-wrapper');
    
    // Teleport the overlay strictly to the main image track
    this.initOverlayPosition();

    this.initToggle();
    this.initLivePreview();
    this.initOverlay();

  }

  initOverlayPosition() {
    const overlayContainer = document.getElementById('monogram-live-overlay');
    const mainCarousel = document.querySelector('.splide.main-carousel');

    if (overlayContainer && mainCarousel && !mainCarousel.contains(overlayContainer)) {
      if (getComputedStyle(mainCarousel).position === 'static') {
        mainCarousel.style.position = 'relative';
      }
      overlayContainer.classList.add('h-full');
      mainCarousel.appendChild(overlayContainer);
    }
  }

  initToggle() {
    if (!this.toggle || !this.content) return;

    const syncToggle = () => {
      this.content.style.display = this.toggle.checked ? 'block' : 'none';
      
      const propertyInputs = this.content.querySelectorAll('input[name^="properties"]');
      propertyInputs.forEach(input => input.disabled = !this.toggle.checked);

      if (this.textInput) this.textInput.required = this.toggle.checked;
      if (this.feeInput) this.feeInput.disabled = !this.toggle.checked;
      
      const finalSaleInput = this.querySelector('input[name="properties[_LPROP]"]');
      if (finalSaleInput) finalSaleInput.disabled = !this.toggle.checked;

      const overlayContainer = document.getElementById('monogram-live-overlay');
      if (overlayContainer) overlayContainer.style.display = this.toggle.checked ? 'block' : 'none';
      
      this.previewWrappers.forEach(wrapper => {
        wrapper.style.display = this.toggle.checked ? 'block' : 'none';
      });

      this.setMonogramSlideActive(this.toggle.checked);
      this.updateOverlay();
    };
    this.toggle.addEventListener('change', syncToggle);
    syncToggle();
  }

  setMonogramSlideActive(active) {
    if (window.innerWidth < 768 && this.querySelector(".monogram-mobile-preview")) return;
    const overlay = document.getElementById('monogram-live-overlay');
    const gallery = (overlay && overlay.closest('gallery-carousel')) || document.querySelector('gallery-carousel');
    const main = gallery && gallery.main;
    const thumbnails = gallery && gallery.thumbnails;
    const slides = main && main.Components && main.Components.Elements.slides;
    if (!slides) return;

    const monogramIndex = slides.findIndex(slide => slide.hasAttribute('data-monogram-image-slide'));
    if (monogramIndex < 0) return;

    if (active) {
      if (this.previousGalleryIndex === null) this.previousGalleryIndex = main.index;
      main.go(monogramIndex);
      if (thumbnails) thumbnails.go(monogramIndex);
      return;
    }

    if (this.previousGalleryIndex !== null) {
      const restoreIndex = Math.min(this.previousGalleryIndex, main.length - 1);
      main.go(restoreIndex);
      if (thumbnails) thumbnails.go(restoreIndex);
      this.previousGalleryIndex = null;
    }
  }

  initLivePreview() {
    this.previewWrappers.forEach(wrapper => {
      wrapper.querySelectorAll('.monogram-preview-text-target').forEach(span => {
        if (!span.dataset.default) span.dataset.default = span.textContent.trim();
      });
    });

    if (this.textInput) {
      this.textInput.addEventListener('input', () => this.updateText());
    }

    if (this.dateInput) {
      this.dateInput.addEventListener('input', () => this.updateDate());
      this.dateInput.addEventListener('change', () => this.updateDate());
    }

    this.updateText();
    this.updateDate();
  }

  textLimit(styleName) {
    return ['Dashes', 'Circle', 'Crest', 'Heart'].includes(styleName) ? 3 : 11;
  }

  updateText() {
    if (!this.textInput) return;
    const selectedStyle = this.querySelector('input[name="properties[Monogram Style]"]:checked');
    const limit = this.textLimit(selectedStyle?.value);
    this.textInput.maxLength = limit;
    this.textInput.placeholder = limit === 3 ? 'Initials (up to 3 letters)' : 'Name (up to 11 letters)';
    this.textInput.value = Array.from(this.textInput.value).slice(0, limit).join('');
    const typedText = this.textInput.value.trim();

    this.previewWrappers.forEach(wrapper => {
      wrapper.querySelectorAll('.monogram-preview-text-target').forEach(span => {
        const previewStyle = span.closest('[data-preview-style]')?.dataset.previewStyle;
        const previewText = typedText === '' ? span.dataset.default : typedText;
        span.textContent = Array.from(previewText || '').slice(0, this.textLimit(previewStyle)).join('');
      });
    });
  }

  updateDate() {
    const rawDate = this.dateInput ? this.dateInput.value.trim() : '';
    const match = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    let parsedDate = null;

    if (match) {
      const month = Number(match[1]);
      const day = Number(match[2]);
      const year = Number(match[3]);
      const candidate = new Date(year, month - 1, day);

      if (
        month >= 1 && month <= 12 &&
        day >= 1 &&
        candidate.getFullYear() === year &&
        candidate.getMonth() === month - 1 &&
        candidate.getDate() === day
      ) {
        parsedDate = { month, day, year };
      }
    }

    this.previewWrappers.forEach(wrapper => {
      wrapper.querySelectorAll('.monogram-preview-date-target').forEach(datePreview => {
        const monthTarget = datePreview.querySelector('.monogram-date-month');
        const dayTarget = datePreview.querySelector('.monogram-date-day');
        const yearTarget = datePreview.querySelector('.monogram-date-year');
        const separators = datePreview.querySelectorAll('.monogram-date-separator');

        if (!parsedDate || !monthTarget || !dayTarget || !yearTarget) {
          datePreview.style.display = 'none';
          datePreview.setAttribute('aria-hidden', 'true');
          return;
        }

        monthTarget.textContent = String(parsedDate.month);
        dayTarget.textContent = String(parsedDate.day);
        yearTarget.textContent = String(parsedDate.year);
        datePreview.style.display = 'inline-flex';
        datePreview.setAttribute('aria-hidden', 'true');
        separators.forEach(separator => separator.style.display = 'inline-flex');
      });
    });
  }

  initOverlay() {
    if (this.content) {
        this.content.addEventListener('change', (e) => {
            if (e.target.name === 'properties[Monogram Style]') {
                this.updateOverlay();
            } else if (e.target.name === 'properties[Monogram Embroidery Color]') {
                this.updateOverlayColor(e.target.value);
            }
        });
        
        const colorLabels = this.content.querySelectorAll('label[data-color]');
        colorLabels.forEach(label => {
            label.addEventListener('click', () => {
                const radio = label.querySelector('input[type="radio"]');
                if (radio) {
                    radio.checked = true;
                    this.updateOverlayColor(radio.value);
                }
            });
        });
    }

    if (this.toggle && this.toggle.checked) this.updateOverlay();
    
    const checkedColor = this.querySelector('input[name="properties[Monogram Embroidery Color]"]:checked');
    if (checkedColor) this.updateOverlayColor(checkedColor.value);
  }

  updateOverlay() {
    const checkedStyle = this.querySelector('input[name="properties[Monogram Style]"]:checked');
    if (!checkedStyle) return;

    const styleName = checkedStyle.value;

    this.previewWrappers.forEach(wrapper => {
      const items = wrapper.querySelectorAll('.monogram-preview-item');
      items.forEach(item => {
        item.style.display = item.dataset.previewStyle === styleName ? 'block' : 'none';
      });
    });

    if (this.textInput) {
       this.updateText();
    }
    this.updateDate();
  }

  updateOverlayColor(colorName) {
    const colorHandle = colorName.toLowerCase().replace(/\s+/g, '-');
    
    this.previewWrappers.forEach(wrapper => {
      wrapper.style.setProperty('--monogram-color', `var(--${colorHandle}, ${colorName})`);
      wrapper.style.color = 'var(--monogram-color)';
    });

    const overlayContainer = document.getElementById('monogram-live-overlay');
    if (overlayContainer) {
      overlayContainer.style.color = 'var(--monogram-color)';
    }
  }

  // ProductForm owns the only cart request; this element validates its details.
  prepareCartData() {
    if (this.cartError) this.cartError.hidden = true;
    if (!this.toggle?.checked) return null;
    this.updateText();
    const feeVariantId = Number(this.feeInput?.value);
    const style = this.querySelector('input[name="properties[Monogram Style]"]:checked');
    const color = this.querySelector('input[name="properties[Monogram Embroidery Color]"]:checked');
    let message = '';
    if (!Number.isSafeInteger(feeVariantId) || feeVariantId <= 0) {
      message = 'The monogram add-on is unavailable. Please contact us for help.';
    } else if (!style || !this.textInput?.value.trim() || !color) {
      message = 'Please choose a monogram style, enter your initials or name, and choose a color.';
    }
    if (message) {
      if (this.cartError) {
        this.cartError.textContent = message;
        this.cartError.hidden = false;
      }
      this.textInput?.focus();
      return false;
    }
    this.textInput.value = this.textInput.value.trim();
    // Read the same values we just validated. FormData excludes disabled fields
    // and fields associated with another form, even when they contain text.
    const properties = {
      'Monogram Order': 'Yes',
      '_LPROP': this.querySelector('input[name="properties[_LPROP]"]')?.value || 'Final Sale',
      'Monogram Style': style.value,
      'Monogram Text': this.textInput.value,
      'Monogram Embroidery Color': color.value,
    };
    if (this.dateInput?.value.trim()) {
      properties['Monogram Birthdate'] = this.dateInput.value.trim();
    }
    return { feeVariantId, properties };
  }
}

if (!customElements.get('monogram-customizer')) {
  customElements.define('monogram-customizer', MonogramCustomizer);
}
